import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { filterQuery, getInsuredListQuery } from './queries';
import { dataToLovs, mapToListingData } from './utils';
import { filterSectionsContent, headers } from './content';
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import { getFilter, setFilter } from '@utils/filter-utils';
import { IAbstractRecord } from '@models';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import StaticLayout from '@components/layout/StaticLayout';
import { IListingData } from '@models/listing';
import DateService from '@services/DateService';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';

const PAGE_CONTEXT = 'Insured';
const FILTER_SESSION_KEY = 'InsuredFilter';

const InsuredPage: React.FC = () => {
  const BPLov = useRef<Record<string, string>>();

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          businessPartner: [],
          createdOn: [],
        };
  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [getInsuredLazy] = useLazyQuery(getInsuredListQuery);
  const [getLovsLazy] = useLazyQuery(filterQuery);

  const getVariables = (filter = filterValues) => {
    const formattedDateRange = DateService.formatDateRangeBackend(
      filter.namedFilters?.createdOn
    );
    return {
      pagination: filter.pagination,
      selectedBusinessPartners: filter.namedFilters.businessPartner,
      Attribute: filter.orderBy || 'Production_Insured_FullName',
      Descending: filter.descending || false,
      KeywordSearch: filter.searchKey || '',
      FromCreatedOn: formattedDateRange?.[0],
      ToCreatedOn: formattedDateRange?.[1],
    };
  };

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      return {};
    }
    const variables = getVariables(filter);
    return getInsuredLazy({
      variables: variables,
      errorPolicy: 'all',
    })
      .then(({ data }) => {
        if (data) {
          const tableData = mapToListingData(
            data,
            filter.namedFilters.businessPartner?.length > 0
              ? filter.namedFilters.businessPartner
              : Object.keys(BPLov.current)
          );

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  const getLovs = async (filter = filterValues) => {
    if (!filter) {
      return;
    }
    const { data: lovsData } = await getLovsLazy();
    const lovs = dataToLovs(lovsData);
    BPLov.current = lovs.businessPartner;
    const newFilterSections = filterSectionsContent(lovs, filter.namedFilters);
    setFilterSections(newFilterSections);
    setFilterValues(filter);
  };

  useEffect(() => {
    getLovs();
  }, []);

  function handlePageChange(
    page: number,
    filterModel = filterValues
  ): Promise<void> {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  }

  const onFilterUpdate = async (filters: IAbstractRecord) => {
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
      pagination: {
        ...filterValues.pagination,
        pageNumber: 1,
      },
    };
    if (isEqual(newFilters, filterValues)) {
      return;
    }
    setFilter(filters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    getListingData(newFilters);
  };

  const renderMainChildren = () => (
    <ListingWidget
      title="Insured"
      name="Insured"
      orderByAscendingByDefault
      inline={false}
      data={tableData}
      tableSettings={{
        headers: headers,
      }}
      defaultOrderByColumn="Production_Insured_FullName"
      pageContext={PAGE_CONTEXT}
      actions={[]}
      usePagination
      disableSelection
      onPageChange={(filterModel) => {
        const newFilterModel = {
          ...filterValues,
          ...filterModel,
        };
        setFilterValues(newFilterModel);
        const page = filterModel.pagination.pageNumber;
        return handlePageChange(page, newFilterModel);
      }}
    />
  );

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name=""
        filters={filterSections}
        onApplyFilter={onFilterUpdate}
      />
    );

  return (
    <StaticLayout
      name="Insured"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default InsuredPage;
